import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TokenValidator } from "@/components/public/TokenValidator";
import { SubmissionTypeSelector } from "@/components/public/SubmissionTypeSelector";
import { SubmissionCapture } from "@/components/public/SubmissionCapture";
import { SubmissionSuccess } from "@/components/public/SubmissionSuccess";
import { FirstConsultationChatbot } from "@/components/public/FirstConsultationChatbot";
import { SatisfactionSurvey } from "@/components/public/SatisfactionSurvey";

type SubmissionType = 'photo' | 'audio' | 'file' | 'text' | null;

export default function AssistedCollectionPublic() {
  const { token } = useParams();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [patientInfo, setPatientInfo] = useState<unknown>(null);
  const [linkInfo, setLinkInfo] = useState<{ link_type?: string; [key: string]: unknown } | null>(null);
  const [selectedType, setSelectedType] = useState<SubmissionType>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token]);

  const validateToken = async () => {
    setIsValidating(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-assisted-collection-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        }
      );

      const data = await response.json();

      if (data.valid) {
        setIsValid(true);
        setPatientInfo(data.patient_info);
        setLinkInfo(data.link_info);
      } else {
        setIsValid(false);
        setError(data.error || 'Link inválido');
      }
    } catch (err: any) {
      setIsValid(false);
      setError('Erro ao validar link');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmissionSuccess = () => {
    setSubmitted(true);
  };

  const handleSubmitAnother = () => {
    setSubmitted(false);
    setSelectedType(null);
  };

  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
              <p className="text-muted-foreground">Validando link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isValid || error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-destructive">Link Inválido</CardTitle>
            <CardDescription>{error || 'Este link não é mais válido.'}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Entre em contato com seu profissional de saúde para obter um novo link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const linkType = linkInfo?.link_type || 'health_data';

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 py-8 px-4">
      <div className="container max-w-4xl mx-auto">
        <TokenValidator patientInfo={patientInfo as { id: string; name: string; avatar_url?: string }} linkInfo={linkInfo as { expires_at: string; submissions_remaining?: number; link_type?: string }} />

        {submitted ? (
          <SubmissionSuccess onSubmitAnother={handleSubmitAnother} />
        ) : linkType === 'first_consultation' ? (
          <FirstConsultationChatbot
            token={token!}
            onSuccess={handleSubmissionSuccess}
          />
        ) : linkType === 'satisfaction_survey' ? (
          <SatisfactionSurvey
            token={token!}
            onSuccess={handleSubmissionSuccess}
          />
        ) : selectedType ? (
          <SubmissionCapture
            token={token!}
            submissionType={selectedType}
            onSuccess={handleSubmissionSuccess}
            onBack={() => setSelectedType(null)}
          />
        ) : (
          <SubmissionTypeSelector onSelectType={setSelectedType} />
        )}
      </div>
    </div>
  );
}
